import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { eur, fmtDate, Loading, EmptyState, StatusBadge } from "@/components/common";

const STATUS_HINT = {
  nuova: "Da programmare", presa_in_carico: "In corso",
  programmata: "Programmata", completata: "Effettuata", annullata: "Annullata",
};

export default function AdminRicorrenze() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [bigMap, setBigMap] = useState(false);

  useEffect(() => {
    api.get("/admin/ricorrenze").then((r) => setData(r.data)).catch(() => setData([]));
  }, []);

  const current = useMemo(() => data?.find((g) => g.recurrence === open) || null, [data, open]);

  const byComune = useMemo(() => {
    if (!current) return [];
    const map = {};
    for (const d of current.deliveries) {
      const k = d.comune || "Senza comune";
      (map[k] = map[k] || []).push(d);
    }
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [current]);

  if (!data) return <Loading />;

  return (
    <div data-testid="admin-ricorrenze">
      <h2 className="font-serif text-2xl text-navy mb-1">Ricorrenze</h2>
      <p className="text-sm text-muted-soft mb-6">
        Fabbisogno prodotti calcolato automaticamente dalle offerte acquistate. Apri una ricorrenza per vedere le consegne raggruppate per comune.
      </p>
      {data.length === 0 ? (
        <EmptyState title="Nessuna ricorrenza venduta" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((g) => (
            <button
              key={g.recurrence}
              type="button"
              data-testid={`ric-card-${g.recurrence}`}
              onClick={() => setOpen(g.recurrence)}
              className="card-soft p-5 text-left hover:shadow-md transition-shadow duration-200"
            >
              <p className="eyebrow mb-2">{g.label}</p>
              <p className="font-serif text-3xl text-navy">{g.units} <span className="text-base">unità vendute</span></p>
              <p className="text-sm text-body mt-1">{g.customers} clienti · {g.pending} da effettuare</p>
            </button>
          ))}
        </div>
      )}

      {current && (
        <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" onClick={() => setOpen(null)} data-testid="ric-list-modal">
          <div className="max-w-2xl mx-auto my-8 bg-white rounded-2xl p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">{current.label}</p>
                <p className="font-serif text-2xl text-navy">Totale: {current.units} consegne</p>
              </div>
              <button type="button" onClick={() => setOpen(null)} data-testid="ric-list-close" className="p-2 text-muted-soft hover:text-navy"><X size={20} /></button>
            </div>
            {byComune.map(([comune, items]) => (
              <div key={comune} className="rounded-2xl border border-[#e2e8f0] p-4" data-testid={`ric-comune-${comune}`}>
                <p className="font-semibold text-navy mb-2 uppercase tracking-wide text-sm">{comune} <span className="text-muted-soft font-normal">— {items.length} consegne</span></p>
                <div className="divide-y divide-[#f1f5f9]">
                  {items.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      data-testid={`ric-delivery-${d.id}`}
                      onClick={() => { setDetail(d); setBigMap(false); }}
                      className="w-full text-left py-2.5 flex items-center justify-between gap-3 hover:bg-[#f8fafc] rounded-lg px-2 -mx-2"
                    >
                      <span className="text-sm text-navy font-medium">{d.customer_name}</span>
                      <span className="text-xs text-muted-soft">{d.deceased_name} {d.deceased_surname} · {STATUS_HINT[d.status] || d.status}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[60] bg-black/60 overflow-y-auto p-4" onClick={() => setDetail(null)} data-testid="ric-detail-modal">
          <div className="max-w-lg mx-auto my-8 bg-white rounded-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">{current?.label}</p>
                <h3 className="font-serif text-2xl text-navy">{detail.customer_name}</h3>
              </div>
              <button type="button" onClick={() => setDetail(null)} data-testid="ric-detail-close" className="p-2 text-muted-soft hover:text-navy"><X size={20} /></button>
            </div>
            <dl className="text-sm space-y-2.5">
              <div className="flex justify-between gap-4"><dt className="text-muted-soft">Prodotto</dt><dd className="text-navy font-medium text-right">{detail.product} · {eur(detail.price ?? 0)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-soft">Comune</dt><dd className="text-navy font-medium">{detail.comune || "—"}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-soft">Cimitero</dt><dd className="text-navy font-medium text-right">{detail.cemetery_name || "—"}{detail.cemetery_address ? ` — ${detail.cemetery_address}` : ""}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-soft">Persona ricordata</dt><dd className="text-navy font-medium">{detail.deceased_name} {detail.deceased_surname}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-soft">Posizione</dt><dd className="text-navy font-medium">Campo {detail.campo || "—"}, fila {detail.fila || "—"}, n. {detail.numero || "—"}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-muted-soft">Data prevista</dt><dd className="text-navy font-medium">{detail.scheduled_date ? fmtDate(detail.scheduled_date) : detail.preferred_date ? `Da programmare (ricorrenza: ${fmtDate(detail.preferred_date)})` : "Da programmare"}</dd></div>
              <div className="flex justify-between gap-4 items-center"><dt className="text-muted-soft">Stato</dt><dd><StatusBadge status={detail.status} /></dd></div>
              {detail.grave_notes && <div><dt className="text-muted-soft mb-1">Note</dt><dd className="text-navy">{detail.grave_notes}</dd></div>}
            </dl>
            {detail.aerial_image && detail.map_x != null && (
              <div className="space-y-2">
                <p className="label-soft">Posizione esatta del luogo</p>
                <button type="button" onClick={() => setBigMap((v) => !v)} data-testid="ric-map-toggle" className="block w-full cursor-zoom-in">
                  <span className="relative inline-block w-full overflow-hidden rounded-xl border border-[#ece7dd] bg-[#eef2f7] text-center">
                    <img src={detail.aerial_image} alt="Mappa del cimitero" className={`block w-auto max-w-full mx-auto object-contain ${bigMap ? "max-h-[70vh]" : "max-h-56"}`} />
                    <span className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ left: `${detail.map_x * 100}%`, top: `${detail.map_y * 100}%` }}>
                      <span className="block h-4 w-4 rounded-full bg-[#4a7c59] border-[3px] border-white shadow-md" />
                    </span>
                  </span>
                </button>
                <p className="text-xs text-muted-soft">{bigMap ? "Tocca per ridurre la mappa." : "Tocca la mappa per ingrandirla."}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
